import { Polynomial } from "../../math/polynomial.js";
import { setIntersection } from "../../util/math.js";
//import { findRoots } from "./cubic-equation.js";
import { CubicCubicIntersections } from "./intersection.js";
/**
 * https://en.wikipedia.org/wiki/Bézier_curve
 * https://en.wikipedia.org/wiki/Cubic_function
 */
export const CubicBezier = {
    getStart: (curve) => {
        return curve.cp[0];
    },
    getEnd: (curve) => {
        return curve.cp[3];
    },
    getDelta: (curve) => {
        return {
            x: curve.cp[3].x - curve.cp[0].x,
            y: curve.cp[3].y - curve.cp[0].y,
        };
    },
    getXRange: (curve) => {
        const xb = CubicBezier.xBounds(curve);
        return [xb.min.x, xb.max.x];
    },
    getYRange: (curve) => {
        const yb = CubicBezier.yBounds(curve);
        return [yb.min.y, yb.max.y];
    },
    /**
     * Returns the position on the curve for given parameter t.
     */
    get: (curve, t) => {
        if (t === 0)
            return curve.cp[0];
        if (t === 1)
            return curve.cp[3];
        const T = t * t;
        const tInv = 1 - t;
        const Tinv = tInv * tInv;
        const c0 = Tinv * tInv;
        const c1 = 3 * Tinv * t;
        const c2 = 3 * tInv * T;
        const c3 = T * t;
        return {
            x: c0 * curve.cp[0].x +
                c1 * curve.cp[1].x +
                c2 * curve.cp[2].x +
                c3 * curve.cp[3].x,
            y: c0 * curve.cp[0].y +
                c1 * curve.cp[1].y +
                c2 * curve.cp[2].y +
                c3 * curve.cp[3].y,
        };
    },
    getDerivative: (curve, t) => {
        const T = t * t;
        const tInv = 1 - t;
        const Tinv = tInv * tInv;
        const c0 = 3 * Tinv;
        const c1 = 6 * tInv * t;
        const c2 = 3 * T;
        return {
            x: c0 * (curve.cp[1].x - curve.cp[0].x) +
                c1 * (curve.cp[2].x - curve.cp[1].x) +
                c2 * (curve.cp[3].x - curve.cp[2].x),
            y: c0 * (curve.cp[1].y - curve.cp[0].y) +
                c1 * (curve.cp[2].y - curve.cp[1].y) +
                c2 * (curve.cp[3].y - curve.cp[2].y),
        };
    },
    getSecondDerivative: (curve, t) => {
        const tInv = 1 - t;
        const c0 = 6 * tInv;
        const c1 = 6 * t;
        return {
            x: c0 * (curve.cp[2].x - 2 * curve.cp[1].x + curve.cp[0].x) +
                c1 * (curve.cp[3].x - 2 * curve.cp[2].x + curve.cp[1].x),
            y: c0 * (curve.cp[2].y - 2 * curve.cp[1].y + curve.cp[0].y) +
                c1 * (curve.cp[3].y - 2 * curve.cp[2].y + curve.cp[1].y),
        };
    },
    tForX: (curve, x) => {
        const rangeX = CubicBezier.xControlBounds(curve);
        if (x < rangeX.min.x || x > rangeX.max.x) {
            return [];
        }
        else {
            const cx = CubicBezier.coefficients(curve.cp, "x");
            cx[0] -= x;
            const roots = Polynomial.rootsClosed(cx, 0.0, 1.0);
            return roots;
        }
    },
    tForY: (curve, y) => {
        const rangeY = CubicBezier.yControlBounds(curve);
        if (y < rangeY.min.y || y > rangeY.max.y) {
            return [];
        }
        else {
            const cy = CubicBezier.coefficients(curve.cp, "y");
            cy[0] -= y;
            const roots = Polynomial.rootsClosed(cy, 0.0, 1.0);
            return roots;
        }
    },
    tDerivative: (curve, dft = 0, coord = "y", tMin = 0.0, tMax = 1.0) => {
        const C = CubicBezier.derivativeCoefficients(curve.cp, coord);
        const precision = CubicBezier.precision(curve.cp);
        const round = CubicBezier.rounder(precision);
        const isZero = CubicBezier.zeroChecker(precision);
        //find the roots
        C[0] -= dft;
        let roots = Polynomial.rootsOpen(C);
        //    let roots: number[] = findRoots(C, dft, isZero);
        if (roots.length === 4) {
            //the curve is a line with a slope equal to dft for the given coordinate
            //we will return t = [ tMin, tMin, tMax, tMax]  or [ 0.0, 0.0, 1.0, 1.0]
            const ts = tMin == undefined ? 0.0 : tMin;
            const te = tMax == undefined ? 1.0 : tMax;
            roots = [ts, ts, te, te];
        }
        return roots
            .map((t) => {
            //only round t when close to 0 or 1
            const tRounded = round(t);
            return tRounded == 0 || tRounded == 1.0 ? tRounded : t;
        })
            .filter((t) => (tMin == undefined || t >= tMin) && (tMax == undefined || t <= tMax))
            .sort();
    },
    tForZeroXDerivative: (curve) => {
        return CubicBezier.tDerivative(curve, 0, "x", 0.0, 1.0);
    },
    tForZeroYDerivative: (curve) => {
        return CubicBezier.tDerivative(curve, 0, "y", 0.0, 1.0);
    },
    isXOnCurve: (curve, x) => {
        //t =0 and t=1 are excluded
        const t = CubicBezier.tForX(curve, x);
        const onCurve = t.length > 0 &&
            t.reduce((onCurve, t) => onCurve || (t > 0.0 && t < 1.0), false);
        return onCurve;
    },
    isYOnCurve: (curve, y) => {
        //t =0 and t=1 are excluded
        const t = CubicBezier.tForY(curve, y);
        return (t.length > 0 &&
            t.reduce((onCurve, t) => onCurve || (t > 0.0 && t < 1.0), false));
    },
    isTOnCurve: (t) => {
        return (t != undefined &&
            t.length > 0 &&
            t.reduce((isAt, t) => isAt && t > 0.0 && t < 1.0, true));
    },
    /**
     * Return t for given point. The point is considered to be on the curve
     * if it is less them Polynomial.DEFAULT_ERROR away from the curve. If the
     * point is not on the curve, undefined gets returned.
     * @param curve The curve to find t for
     * @param p The point to test
     * @param tolerance the allowed tolerance
     * @return the t of the point on the curve or undefined when the point is not on the curve
     */
    getTForPoint: (curve, p, tolerance = 0.0) => {
        const tForX = CubicBezier.tForX(curve, p.x);
        const tForY = CubicBezier.tForY(curve, p.y);
        const tForPoint = setIntersection(tForX, tForY, tolerance);
        return tForPoint[0];
    },
    hasTAtStart: (t) => {
        return (t != undefined &&
            t.length > 0 &&
            t.reduce((isAt, t) => isAt || t == 0, false));
    },
    hasTAtEnd: (t) => {
        return (t != undefined &&
            t.length > 0 &&
            t.reduce((isAt, t) => isAt || t == 1, false));
    },
    tIntersect: (curve0, curve1) => {
        const precision = [curve0, curve1]
            .map((curve) => CubicBezier.precision(curve.cp))
            .reduce((max, precision) => Math.max(max, precision), 0);
        const rounder = CubicBezier.rounder(precision);
        return CubicCubicIntersections.findIntersectionsT(curve0, curve1, rounder);
    },
    t: (curve, line, tMin = 0.0, tMax = 1.0) => {
        const precision = CubicBezier.precision(curve.cp);
        const round = CubicBezier.rounder(precision);
        const isZero = CubicBezier.zeroChecker(precision);
        const cubicLine = CubicBezier.createLine(line.p1, line.p2);
        let roots = CubicCubicIntersections.findIntersectionsT(curve, cubicLine, round);
        // //Align (translate and rotate) with the line
        // const aligned = CubicBezier.getAligned(curve.cp, line);
        // // and rewrite from Bernstein form [f(t) = a(1-t)^3 + 3bt(1-t)^2 + 3c(1-t)t^2 + dt^3]
        // // to polynomial form [f(t) =c3*t^3 + c2*t^2 + c1*t^ + c0].
        // // Since the curve is now aligned (translated and rotated) with the
        // // x-axis (the intersection line has now become the x-axis)
        // // we only need f(t) for the y coordinates and find the roots where y(t) = 0,
        // // Get the polynomial coefficients
        // const C = CubicBezier.coefficients(aligned, "y");
        // //find the roots
        // let roots: number[] = findRoots(C, 0.0, isZero);
        if (roots.length === 4) {
            //the line and the curve are on top of each other
            //we will return t = [ tMin, tMin, tMax, tMax]  or [ 0.0, 0.0, 1.0, 1.0]
            const ts = tMin == undefined ? 0.0 : tMin;
            const te = tMax == undefined ? 1.0 : tMax;
            roots = [ts, ts, te, te];
        }
        return roots
            .map((t) => {
            //only round t when close to 0 or 1
            const tRounded = round(t);
            return tRounded == 0 || tRounded == 1.0 ? tRounded : t;
        })
            .filter((t) => (tMin == undefined || t >= tMin) && (tMax == undefined || t <= tMax))
            .sort();
    },
    split: (curve, t) => {
        if (t <= 0 || t >= 1) {
            throw new Error(`Can not make split for t = ${t}:  0 < t < 1`);
        }
        let xCBb0p1 = (curve.cp[1].x - curve.cp[0].x) * t + curve.cp[0].x;
        let yCBb0p1 = (curve.cp[1].y - curve.cp[0].y) * t + curve.cp[0].y;
        let xHelper = (curve.cp[2].x - curve.cp[1].x) * t + curve.cp[1].x;
        let yHelper = (curve.cp[2].y - curve.cp[1].y) * t + curve.cp[1].y;
        let xCBb1p2 = (curve.cp[3].x - curve.cp[2].x) * t + curve.cp[2].x;
        let yCBb1p2 = (curve.cp[3].y - curve.cp[2].y) * t + curve.cp[2].y;
        let xCBb0p2 = (xHelper - xCBb0p1) * t + xCBb0p1;
        let yCBb0p2 = (yHelper - yCBb0p1) * t + yCBb0p1;
        let xCBb1p1 = (xCBb1p2 - xHelper) * t + xHelper;
        let yCBb1p1 = (yCBb1p2 - yHelper) * t + yHelper;
        let xSlice = (xCBb1p1 - xCBb0p2) * t + xCBb0p2;
        let ySlice = (yCBb1p1 - yCBb0p2) * t + yCBb0p2;
        return {
            curve0: {
                cp: [
                    { x: curve.cp[0].x, y: curve.cp[0].y },
                    { x: xCBb0p1, y: yCBb0p1 },
                    { x: xCBb0p2, y: yCBb0p2 },
                    { x: xSlice, y: ySlice },
                ],
                length: curve.length * t,
                tag: curve.tag,
            },
            curve1: {
                cp: [
                    { x: xSlice, y: ySlice },
                    { x: xCBb1p1, y: yCBb1p1 },
                    { x: xCBb1p2, y: yCBb1p2 },
                    { x: curve.cp[3].x, y: curve.cp[3].y },
                ],
                length: curve.length * (1.0 - t),
                tag: curve.tag,
            },
        };
    },
    splitAtX: (curve, x) => {
        //find t's for x
        const t = CubicBezier.tForX(curve, x);
        //to get rid of rounding issues -> snap the relevant x coordinates to the given x
        const postSplitProcessor = (curves) => {
            if (curves.length >= 2) {
                curves.at(-2).cp[3].x = x;
                curves.at(-1).cp[0].x = x;
            }
        };
        //make the splits
        const splittedCurve = CubicBezier.splitAtT(curve, t, postSplitProcessor);
        return splittedCurve;
    },
    splitAtY: (curve, y) => {
        //find t's for y
        const t = CubicBezier.tForY(curve, y);
        //to get rid of rounding issues -> snap the relevant y coordinates to the given y
        const postSplitProcessor = (curves) => {
            if (curves.length >= 2) {
                curves.at(-2).cp[3].y = y;
                curves.at(-1).cp[0].y = y;
            }
        };
        //make the splits
        const splittedCurve = CubicBezier.splitAtT(curve, t, postSplitProcessor);
        return splittedCurve;
    },
    splitAtT: (curve, t, postSplitProcessor) => {
        //we can only make splits for 0 < t < 1
        const tSplits = t.filter((_t) => _t > 0.0 && _t < 1.0);
        return tSplits.reduce((accu, t) => {
            //adjust t for splits that come after the first split
            const tSplit = (t - accu.tPrevious) / (1 - accu.tPrevious);
            //split the curve
            const curveToSplit = accu.sections.splice(-1, 1)[0];
            const splittedCurve = CubicBezier.split(curveToSplit, tSplit);
            //put results into the accumulator
            accu.sections.push(splittedCurve.curve0);
            accu.sections.push(splittedCurve.curve1);
            accu.tPrevious = t;
            //possibly do some post processing
            if (postSplitProcessor) {
                postSplitProcessor(accu.sections);
            }
            return accu;
        }, {
            sections: [curve],
            tPrevious: 0.0,
        }).sections;
    },
    flatness: (cp) => {
        return (Math.max(Math.pow(3.0 * cp[1].x - 2.0 * cp[0].x - cp[3].x, 2.0), Math.pow(3.0 * cp[2].x - 2.0 * cp[3].x - cp[0].x, 2.0)) +
            Math.max(Math.pow(3.0 * cp[1].y - 2.0 * cp[0].y - cp[3].y, 2.0), Math.pow(3.0 * cp[2].y - 2.0 * cp[3].y - cp[0].y, 2.0)));
    },
    linearize: (curve, flatness) => {
        if (CubicBezier.flatness(curve.cp) <= 16 * flatness * flatness) {
            return [curve.cp[0], curve.cp[3]];
        }
        else {
            let splittedCurve = CubicBezier.split(curve, 0.5);
            return CubicBezier.linearize(splittedCurve.curve0, flatness).concat(CubicBezier.linearize(splittedCurve.curve1, flatness).slice(1));
        }
    },
    getAligned: (cp, line) => {
        const tx = line.p1.x, ty = line.p1.y, a = -Math.atan2(line.p2.y - ty, line.p2.x - tx), cosa = Math.cos(a), sina = Math.sin(a);
        const alignedCurveControlPoints = cp.map((v) => {
            return {
                x: (v.x - tx) * cosa - (v.y - ty) * sina,
                y: (v.x - tx) * sina + (v.y - ty) * cosa,
            };
        });
        return alignedCurveControlPoints;
    },
    path: (curves, xScale = (v) => v, yScale = (v) => v) => {
        let path = [];
        if (curves && curves.length > 0) {
            path.push(`M${xScale(curves[0].cp[0].x)} ${yScale(curves[0].cp[0].y)}`);
            curves.forEach((curve) => {
                path.push(`C${xScale(curve.cp[1].x)} ${yScale(curve.cp[1].y)} ${xScale(curve.cp[2].x)} ${yScale(curve.cp[2].y)} ${xScale(curve.cp[3].x)} ${yScale(curve.cp[3].y)}`);
            });
        }
        return path.join("");
    },
    clone: (curve) => {
        return {
            cp: (curve.cp.map((point) => ({ x: point.x, y: point.y }))),
            length: curve.length,
            tag: curve.tag,
        };
    },
    reverse: (curve) => {
        curve.cp.reverse();
        return curve;
    },
    equalStart: (curve0, curve1) => {
        return CubicBezier.coordinateChecker(curve0, curve1)(curve0.cp[0])(curve1.cp[0]);
    },
    equalEnd: (curve0, curve1) => {
        return CubicBezier.coordinateChecker(curve0, curve1)(curve0.cp[3])(curve1.cp[3]);
    },
    equalEndings: (curve0, curve1) => {
        const checkerFactory = CubicBezier.coordinateChecker(curve0, curve1);
        const s0Checker = checkerFactory(curve0.cp[0]);
        const e0Checker = checkerFactory(curve0.cp[3]);
        return ((s0Checker(curve1.cp[3]) && e0Checker(curve1.cp[0])) ||
            (s0Checker(curve1.cp[0]) && e0Checker(curve1.cp[1])));
    },
    coordinateChecker: (curve0, curve1) => {
        const precision = Math.max(CubicBezier.precision(curve0.cp), CubicBezier.precision(curve1.cp));
        const isZero = CubicBezier.zeroChecker(precision);
        return (c0) => (c1) => isZero(c0.x - c1.x) && isZero(c0.y - c1.y);
    },
    overlap: (curve0, curve1) => {
        return CubicBezier.yOverlap(curve0, curve1) && CubicBezier.xOverlap(curve0, curve1);
    },
    xOverlap: (curve0, curve1) => {
        let bounds0 = CubicBezier.xControlBounds(curve0);
        let bounds1 = CubicBezier.xControlBounds(curve1);
        let overlap = bounds0.max.x >= bounds1.min.x && bounds0.min.x <= bounds1.max.x;
        if (overlap) {
            bounds0 = CubicBezier.xBounds(curve0);
            bounds1 = CubicBezier.xBounds(curve1);
            overlap =
                bounds0.max.x >= bounds1.min.x && bounds0.min.x <= bounds1.max.x;
        }
        return overlap;
    },
    yOverlap: (curve0, curve1) => {
        let bounds0 = CubicBezier.yControlBounds(curve0);
        let bounds1 = CubicBezier.yControlBounds(curve1);
        let overlap = bounds0.max.y >= bounds1.min.y && bounds0.min.y <= bounds1.max.y;
        if (overlap) {
            bounds0 = CubicBezier.yBounds(curve0);
            bounds1 = CubicBezier.yBounds(curve1);
            overlap =
                bounds0.max.y >= bounds1.min.y && bounds0.min.y <= bounds1.max.y;
        }
        return overlap;
    },
    bounds: (curve) => {
        const b = CubicBezier.xBounds(curve);
        const by = CubicBezier.yBounds(curve);
        b.min.y = by.min.y;
        b.max.y = by.max.y;
        return b;
    },
    xBounds: (curve) => {
        const bounds = {
            min: { x: Math.min(curve.cp[0].x, curve.cp[3].x) },
            max: { x: Math.max(curve.cp[0].x, curve.cp[3].x) },
        };
        const vRoots = CubicBezier.tForZeroXDerivative(curve);
        if (vRoots.length > 0 && vRoots.length < 4) {
            vRoots
                .map((t) => CubicBezier.get(curve, t))
                .forEach((p) => {
                bounds.min.x = Math.min(bounds.min.x, p.x);
                bounds.max.x = Math.max(bounds.max.x, p.x);
            });
        }
        return bounds;
    },
    yBounds: (curve) => {
        const bounds = {
            min: { y: Math.min(curve.cp[0].y, curve.cp[3].y) },
            max: { y: Math.max(curve.cp[0].y, curve.cp[3].y) },
        };
        const hRoots = CubicBezier.tForZeroYDerivative(curve);
        if (hRoots.length > 0 && hRoots.length < 4) {
            hRoots
                .map((t) => CubicBezier.get(curve, t))
                .forEach((p) => {
                bounds.min.y = Math.min(bounds.min.y, p.y);
                bounds.max.y = Math.max(bounds.max.y, p.y);
            });
        }
        return bounds;
    },
    controlBounds: (curve) => {
        const b = CubicBezier.xControlBounds(curve);
        const by = CubicBezier.yControlBounds(curve);
        b.min.y = by.min.y;
        b.max.y = by.max.y;
        return b;
    },
    xControlBounds: (curve) => {
        return curve.cp.reduce((bounds, p) => {
            bounds.min.x = Math.min(bounds.min.x, p.x);
            bounds.max.x = Math.max(bounds.max.x, p.x);
            return bounds;
        }, {
            min: { x: Number.POSITIVE_INFINITY },
            max: { x: Number.NEGATIVE_INFINITY },
        });
    },
    yControlBounds: (curve) => {
        return curve.cp.reduce((bounds, p) => {
            bounds.min.y = Math.min(bounds.min.y, p.y);
            bounds.max.y = Math.max(bounds.max.y, p.y);
            return bounds;
        }, {
            min: { y: Number.POSITIVE_INFINITY },
            max: { y: Number.NEGATIVE_INFINITY },
        });
    },
    coefficients: (cp, coord) => {
        //f(t) = c3*t^3 + c2*t^2 + c1*t + c0
        // where:
        // c0 = p0
        // c1 = -3 * p0 + 3 * p1
        // c2 = 3 * p0 - 6 * p1 + 3 * p2
        // c3 = -p0 + 3 * p1 + -3 * p2 + p3
        const p0 = cp[0][coord], p1 = cp[1][coord], p2 = cp[2][coord], p3 = cp[3][coord];
        return [
            p0,
            -3 * p0 + 3 * p1,
            3 * p0 - 6 * p1 + 3 * p2,
            -p0 + 3 * p1 + -3 * p2 + p3,
        ];
    },
    derivativeCoefficients: (cp, coord) => {
        //f`(t) = c2`*t^2 + c1`*t + c0`
        // where:
        // c0` = c1
        // c1` = 2*c2
        // c2` = 3*c3
        const p0 = cp[0][coord], p1 = cp[1][coord], p2 = cp[2][coord], p3 = cp[3][coord];
        return [
            -3 * p0 + 3 * p1,
            2.0 * (3 * p0 - 6 * p1 + 3 * p2),
            3 * (-p0 + 3 * p1 + -3 * p2 + p3),
        ];
    },
    secondDerivativeCoefficients: (cp, coord) => {
        //f``t) = c1``*t + c0`
        // where:
        // c0`` = c1` = 2*c2
        // c1`` = 2*c2` = 2 * ( 3 * c3 ) = 6*c3
        const p0 = cp[0][coord], p1 = cp[1][coord], p2 = cp[2][coord], p3 = cp[3][coord];
        return [
            2.0 * (3 * p0 - 6 * p1 + 3 * p2),
            6.0 * (-p0 + 3 * p1 + -3 * p2 + p3),
        ];
    },
    precision: (curve) => {
        const l = (p0, p1) => Math.sqrt((p1.x - p0.x) * (p1.x - p0.x) + (p1.y - p0.y) * (p1.y - p0.y));
        const l0 = l(curve[0], curve[1]);
        const l1 = l(curve[1], curve[2]);
        const l2 = l(curve[2], curve[3]);
        const precision = Math.ceil(Math.log10(l0 + l1 + l2));
        return precision;
    },
    rounder: (precision) => {
        const _precision = precision.length !== undefined
            ? CubicBezier.precision(precision)
            : precision;
        const precisionFactor = Math.pow(10, _precision);
        return (v) => Math.round(v * precisionFactor) / precisionFactor;
    },
    zeroChecker: (precision) => {
        const zeroBoundary = 1 / Math.pow(10, precision + 1);
        return (v) => Math.abs(v) < zeroBoundary;
    },
    avoidZero: (precision) => {
        const minAbsD = 1 / Math.pow(10, precision + 1);
        return (d) => Math.abs(d) < minAbsD ? (d < 0 ? -minAbsD : minAbsD) : d;
    },
    createLine: (p0, p1) => {
        const d = { x: (p1.x - p0.x) / 3.0, y: (p1.y - p0.y) / 3.0 };
        return {
            cp: [
                p0,
                { x: p0.x + d.x, y: p0.y + d.y },
                { x: p1.x - d.x, y: p1.y - d.y },
                p1,
            ],
            length: 1.0,
        };
    },
    translate: (curve, dx = 0.0, dy = 0.0) => {
        return {
            cp: curve.cp.map((p) => ({ x: p.x + dx, y: p.y + dy })),
            length: curve.length,
            tag: curve.tag,
        };
    },
    rePosition: (curve, p, start = true) => {
        const i = start ? 0 : 3;
        const dx = p.x - curve.cp[i].x;
        const dy = p.y - curve.cp[i].y;
        return CubicBezier.translate(curve, dx, dy);
    },
    // implicit: (
    //   cp: BezierCurveControlPoints
    // ): [
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    //   number,
    // ] => {
    //   // Implicitization of a general parametric cubic curve
    //   // from x=A(t), y=B(t) to C(x,y)=0
    //   // where A(t) = a0+a1*t+a2*t^2+a3*t^3, B(t)=b0+b1*t+b2*t^2+b3*t^3 and
    //   // C(x,y)=C1+Cx*x+Cy*y+Cx2*x^2+Cxy*x*y+Cy2*y^2+Cx3*x^3+Cx2y*x^2*y+Cxy2*x*y^2+Cy3*y^3
    //   //
    //   // The a, b coefficients are stored in one vec4 each,
    //   // and the C coefficients are stored in an array:
    //   // vec4 a = vec4(a0, a1, a2, a3)
    //   // vec4 b = vec4(b0, b1, b2, b3)
    //   // c[10] = float[](C1, Cx, Cy, Cx2, Cxy, Cy2, Cx3, Cx2y, Cxy2, Cy3)
    //   //
    //   // The array c[10] is not a natural fit for GLSL. Packing the ten
    //   // coefficients into three vec4 variables or a matrix could be a
    //   // better option for speed. A float RGB texture could store the
    //   // coefficients in four texels. There are many options.
    //   // This is barely-ported Matlab/Maple code from 2006.
    //   // Hopefully, the compiler can sink its optimization teeth into it,
    //   // but I should write this on a form better suited for numerical
    //   // computation, to make sure there are no unnecessary operations
    //   // and possibly improve accuracy and numerical stability.
    //   const a = CubicBezier.coefficients(cp, "x");
    //   const b = CubicBezier.coefficients(cp, "y");
    //   const a2 = a.map((a) => a * a);
    //   const a3 = a2.map((a2, i) => a2 * a[i]);
    //   const b2 = b.map((b) => b * b);
    //   const b3 = b2.map((b2, i) => b2 * b[i]);
    //   const ab = a.map((a, i) => a * b[i]);
    //   const c: [
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //     number,
    //   ] = [
    //     // C1
    //     ab[1] * ab[0] * a[2] * b[3] -
    //       3.0 * a2[1] * ab[0] * b[3] * b[2] -
    //       a3[1] * b3[0] +
    //       3.0 * a[0] * b[1] * a2[1] * b2[0] +
    //       2.0 * a2[1] * b2[0] * a[3] * b[2] +
    //       a3[0] * b3[1] -
    //       ab[0] * ab[1] * a[3] * b[2] +
    //       3.0 * a2[0] * ab[1] * b[3] * b[2] +
    //       3.0 * ab[0] * b2[1] * a[3] * a[2] +
    //       a[2] * b2[0] * a2[1] * b[3] -
    //       2.0 * ab[0] * b[2] * a2[2] * b[1] +
    //       2.0 * ab[0] * b2[2] * a[2] * a[1] -
    //       a2[0] * b3[2] * a[1] +
    //       a3[2] * b2[0] * b[1] +
    //       a2[0] * b2[2] * a[2] * b[1] -
    //       3.0 * ab[1] * b2[0] * a[3] * a[2] +
    //       a[0] * b[3] * a2[3] * b2[1] +
    //       a[0] * b2[3] * a2[2] * b[1] -
    //       a2[0] * b[2] * a[3] * b2[1] -
    //       a2[2] * b2[0] * b[2] * a[1] +
    //       a[0] * b3[3] * a2[1] -
    //       2.0 * a[0] * b2[3] * a[3] * ab[1] -
    //       3.0 * a2[0] * b2[1] * a[1] * b[0] -
    //       2.0 * a2[0] * b2[1] * a[2] * b[3] -
    //       ab[3] * b[0] * a2[2] * b[1] +
    //       a2[3] * b[0] * ab[2] * b[1] +
    //       ab[3] * b[0] * ab[2] * a[1] +
    //       2.0 * a2[3] * b[0] * ab[1] * b[3] -
    //       a[3] * b[0] * a2[1] * b2[3] -
    //       a2[3] * b[0] * b2[2] * a[1] +
    //       a[0] * ab[3] * b2[2] * a[1] -
    //       a[0] * b2[3] * ab[2] * a[1] -
    //       a[0] * ab[3] * ab[2] * b[1] -
    //       a3[3] * b[0] * b2[1],
    //     // Cx
    //     -2.0 * a[2] * b2[0] * b[3] * a[1] -
    //       3.0 * a2[0] * b[1] * b[3] * b[2] -
    //       ab[0] * a[2] * b[3] * b[1] +
    //       3.0 * b3[0] * a2[1] -
    //       2.0 * ab[0] * b2[2] * a[2] +
    //       6.0 * ab[0] * a[1] * b[3] * b[2] +
    //       a2[2] * b2[0] * b[2] +
    //       a2[0] * b3[2] +
    //       3.0 * a2[0] * b2[1] * b[0] +
    //       2.0 * a[0] * b2[3] * a[3] * b[1] -
    //       a[0] * ab[3] * b2[2] +
    //       a2[3] * b[0] * b2[2] +
    //       a[0] * b2[3] * ab[2] -
    //       2.0 * a2[3] * b[0] * b[1] * b[3] -
    //       4.0 * b2[0] * a[3] * b[2] * a[1] -
    //       ab[3] * b[0] * ab[2] +
    //       2.0 * a[3] * b[0] * b2[3] * a[1] -
    //       2.0 * a[0] * b3[3] * a[1] -
    //       6.0 * a[0] * b[1] * b2[0] * a[1] +
    //       ab[0] * b[1] * a[3] * b[2] +
    //       3.0 * b2[0] * a[3] * b[1] * a[2],
    //     // Cy
    //     6.0 * a2[0] * ab[1] * b[0] -
    //       3.0 * a2[0] * a[1] * b[3] * b[2] +
    //       2.0 * a[0] * b2[3] * a[3] * a[1] -
    //       3.0 * a3[0] * b2[1] -
    //       2.0 * a[0] * b[3] * a2[3] * b[1] +
    //       2.0 * ab[0] * b[2] * a2[2] +
    //       ab[0] * a[3] * b[2] * a[1] +
    //       3.0 * a[1] * b2[0] * a[3] * a[2] +
    //       2.0 * a3[3] * b[0] * b[1] -
    //       a2[0] * b2[2] * a[2] -
    //       a[1] * ab[0] * a[2] * b[3] -
    //       3.0 * a[0] * a2[1] * b2[0] -
    //       a2[3] * b[0] * ab[2] +
    //       ab[3] * b[0] * a2[2] -
    //       2.0 * a2[3] * b[0] * a[1] * b[3] -
    //       6.0 * ab[0] * a[3] * b[1] * a[2] +
    //       4.0 * a2[0] * a[2] * b[3] * b[1] -
    //       a3[2] * b2[0] -
    //       a[0] * b2[3] * a2[2] +
    //       2.0 * a2[0] * b[2] * a[3] * b[1] +
    //       a[0] * ab[3] * ab[2],
    //     // Cx2
    //     -3.0 * b3[0] * a[1] -
    //       a[3] * b[0] * b2[3] +
    //       2.0 * b2[0] * a[3] * b[2] +
    //       3.0 * a[0] * b[1] * b2[0] -
    //       3.0 * ab[0] * b[3] * b[2] +
    //       a[0] * b3[3] +
    //       a[2] * b2[0] * b[3],
    //     // Cxy
    //     -2.0 * a[0] * b2[3] * a[3] +
    //       2.0 * a2[3] * b[0] * b[3] -
    //       6.0 * a2[0] * b[1] * b[0] +
    //       6.0 * a[0] * b2[0] * a[1] +
    //       3.0 * a2[0] * b[3] * b[2] -
    //       ab[0] * a[3] * b[2] +
    //       ab[0] * a[2] * b[3] -
    //       3.0 * b2[0] * a[3] * a[2],
    //     // Cy2
    //     -a2[0] * b[2] * a[3] +
    //       3.0 * ab[0] * a[3] * a[2] +
    //       a[0] * b[3] * a2[3] +
    //       3.0 * a3[0] * b[1] -
    //       2.0 * a2[0] * a[2] * b[3] -
    //       3.0 * a2[0] * a[1] * b[0] -
    //       a3[3] * b[0],
    //     // Cx3
    //     b3[0],
    //     // Cx2y
    //     -3.0 * a[0] * b2[0],
    //     // Cxy2
    //     3.0 * a2[0] * b[0],
    //     // Cy3
    //     -a3[0],
    //   ];
    //   return c;
    // },
};
